import { Moment } from 'moment-timezone';

import { ApiResources } from 'types/apiResources';
import { CookieKeys } from 'types/cookies';
import { Endpoints } from 'types/endpoints';

import Order, { CreateMethod, UserTypes } from 'models/Order';
import OrderPayment from 'models/OrderPayment';
import PaymentTransaction, { PaymentProvider } from 'models/PaymentTransaction';
import { Types } from 'models/UserAgree';

import { withVariables } from 'utils/string';
import { request } from 'services/Api';
import { getProvider } from 'services/Cookies';

export interface CreateParams {
    externalMethodId?: number;
    channelId?: number;
    paymentProvider: PaymentProvider;
    createMethod?: CreateMethod;
    userType: UserTypes;
    userId?: string;
    cartId?: string;
    userAgrees: {
        type: Types;
        value: boolean;
        content: string | React.ReactElement;
    }[];
    discountCodeId?: string;
    products?: {
        productId: string;
        quantity: number;
        locationId: string;
    }[];
    events?: {
        productIds?: string[];
        locationId: string;
        nurseId?: string;
        startAt: Moment;
        deviceModelId?: string;
        commentOffice?: string;
        commentClient?: string;
    }[];
    vouchers?: {
        voucherType: string;
        voucherValue: number;
        voucherPrice?: number;
        voucherFirstName: string;
        voucherLastName: string;
        voucherText?: string;
        voucherEmail?: string;
        voucherLocationId?: string;
        voucherId?: string;
    }[];
    bundles?: {
        bundleId: string;
        quantity: number;
        locationId: string;
    }[];
    orderUser: {
        firstName?: string;
        lastName?: string;
        email?: string;
        phonePrefix?: string;
        phone?: string;
        address?: string;
        city?: string;
        postalCode?: string;
        gender?: string;
    };
    orderInvoice?: {
        vatId?: string;
        companyName?: string;
        country?: string;
        city?: string;
        address?: string;
        postalCode?: string;
    };
    userSource?: {
        category?: string;
        subcategory?: string;
        adid?: string;
        source?: string;
        medium?: string;
        campaign?: string;
        term?: string;
        content?: string;
        referrer?: string;
        payload?: string;
        locationId?: string;
    };
}

export interface CreateResponse {
    order: Order;
    orderPayment: OrderPayment;
    paymentTransaction: PaymentTransaction;
}

export const create = async (params?: CreateParams): Promise<CreateResponse> => {
    let response = null;
    try {
        const marketingParams = getProvider().get(CookieKeys.AttributionLast);
        if (marketingParams) {
            params = {
                ...params,
                userSource: {
                    category: 'site',
                    subcategory: 'order',
                    adid: marketingParams?.adid || null,
                    source: marketingParams?.utm_source || null,
                    medium: marketingParams?.utm_medium || null,
                    campaign: marketingParams?.utm_campaign || null,
                    term: marketingParams?.utm_term || null,
                    content: marketingParams?.utm_content || null,
                    referrer: document.referrer,
                },
            };
        }

        response = await request({
            method: 'POST',
            path: withVariables(Endpoints.PublicOrders, params),
            requestParams: params,
        });
    } catch (error) {
        console.error(error);
        throw error;
    }

    const data = response?.payload?.data;
    if (!data) {
        throw new Error('Not Found');
    }

    return {
        order: data[ApiResources.Order] && new Order(data[ApiResources.Order]),
        orderPayment: data[ApiResources.OrderPayment] && new OrderPayment(data[ApiResources.OrderPayment]),
        paymentTransaction: data[ApiResources.PaymentTransaction] && new PaymentTransaction(data[ApiResources.PaymentTransaction]),
    };
};
